// Public read of a single helper profile bundle (helper + profile + skills + reviews).
// Runs through the user's RLS context so suspended helpers are hidden automatically.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type HelperReview = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  reviewer_name: string | null;
  reviewer_avatar: string | null;
  category_name: string | null;
};

export type HelperProfileBundle = {
  id: string;
  display_name: string;
  full_name: string | null;
  avatar_url: string | null;
  city: string | null;
  bio: string | null;
  headline: string | null;
  about: string | null;
  hourly_rate: number | null;
  service_radius_miles: number | null;
  base_postcode: string | null;
  years_experience: number | null;
  instant_book_enabled: boolean;
  available: boolean;
  verification_status: string | null;
  id_verified: boolean;
  background_check_passed: boolean;
  jobs_completed: number;
  avg_rating: number;
  rating_count: number;
  response_time_minutes: number | null;
  acceptance_rate: number | null;
  completion_rate: number | null;
  repeat_booking_rate: number | null;
  featured: boolean;
  trust_score: number;
  trust_score_updated_at: string | null;
  created_at: string;
  skills: { id: string; slug: string; name: string; icon: string | null }[];
  badges: { id: string; slug: string; name: string; icon: string | null }[];
  reviews: HelperReview[];
  rating_breakdown: Record<1 | 2 | 3 | 4 | 5, number>;
};

export const getHelperProfile = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ helperId: z.string().uuid() }).parse(input))
  .handler(async ({ data }): Promise<HelperProfileBundle | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: helper, error: helperErr } = await supabaseAdmin
      .from("helper_profiles")
      .select(
        "id, headline, about, hourly_rate, service_radius_miles, base_postcode, years_experience, instant_book_enabled, available, verification_status, id_verified, background_check_passed, jobs_completed, avg_rating, rating_count, response_time_minutes, acceptance_rate, completion_rate, repeat_booking_rate, featured, created_at, suspended, trust_score, trust_score_updated_at"
      )
      .eq("id", data.helperId)
      .maybeSingle();
    if (helperErr || !helper || helper.suspended || helper.verification_status !== "verified") return null;

    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("id, full_name, display_name, avatar_url, city, bio")
      .eq("id", data.helperId)
      .maybeSingle();

    const { data: skillsRaw } = await supabaseAdmin
      .from("helper_skills")
      .select("category_id, categories:category_id(id, slug, name, icon)")
      .eq("helper_id", data.helperId);

    const { data: badgesRaw } = await supabaseAdmin
      .from("user_badges")
      .select("badges:badge_id(id, slug, name, icon)")
      .eq("user_id", data.helperId);

    const { data: reviewsRaw } = await supabaseAdmin
      .from("reviews")
      .select("id, rating, comment, created_at, reviewer_id, booking_id")
      .eq("reviewee_id", data.helperId)
      .eq("is_public", true)
      .eq("flagged", false)
      .eq("reviewer_type", "customer")
      .order("created_at", { ascending: false })
      .limit(40);

    // Fetch reviewer names + category for each review in one extra round-trip.
    const reviewerIds = Array.from(new Set((reviewsRaw ?? []).map((r) => r.reviewer_id).filter(Boolean)));
    const bookingIds = Array.from(new Set((reviewsRaw ?? []).map((r) => r.booking_id).filter(Boolean)));
    const [{ data: reviewerProfiles }, { data: bookingRows }] = await Promise.all([
      reviewerIds.length
        ? supabaseAdmin.from("profiles").select("id, display_name, full_name, avatar_url").in("id", reviewerIds)
        : Promise.resolve({ data: [] as Array<{ id: string; display_name: string | null; full_name: string | null; avatar_url: string | null }> }),
      bookingIds.length
        ? supabaseAdmin
            .from("bookings")
            .select("id, job_id, jobs:job_id(category_id, categories:category_id(name))")
            .in("id", bookingIds)
        : Promise.resolve({ data: [] as Array<{ id: string; jobs: { categories: { name: string | null } | null } | null }> }),
    ]);
    const reviewerMap = new Map((reviewerProfiles ?? []).map((p: any) => [p.id, p]));
    const bookingMap = new Map((bookingRows ?? []).map((b: any) => [b.id, b]));

    const reviews: HelperReview[] = (reviewsRaw ?? []).map((r) => {
      const reviewer = reviewerMap.get(r.reviewer_id);
      const booking = bookingMap.get(r.booking_id);
      const reviewerName = reviewer
        ? (reviewer.display_name as string | null) || (reviewer.full_name as string | null) || null
        : null;
      // Anonymise: surname-initial only
      const safe = reviewerName ? reviewerName.split(" ").map((w, i, arr) => (i === arr.length - 1 && arr.length > 1 ? `${w[0]}.` : w)).join(" ") : null;
      return {
        id: r.id,
        rating: Number(r.rating),
        comment: r.comment,
        created_at: r.created_at,
        reviewer_name: safe,
        reviewer_avatar: reviewer?.avatar_url ?? null,
        category_name: booking?.jobs?.categories?.name ?? null,
      };
    });

    const breakdown: Record<1 | 2 | 3 | 4 | 5, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (const r of reviews) {
      const k = Math.max(1, Math.min(5, Math.round(r.rating))) as 1 | 2 | 3 | 4 | 5;
      breakdown[k]++;
    }

    return {
      id: helper.id,
      display_name: (profile?.display_name as string | null) || (profile?.full_name as string | null) || "Helper",
      full_name: (profile?.full_name as string | null) ?? null,
      avatar_url: (profile?.avatar_url as string | null) ?? null,
      city: (profile?.city as string | null) ?? null,
      bio: (profile?.bio as string | null) ?? null,
      headline: helper.headline,
      about: helper.about,
      hourly_rate: helper.hourly_rate != null ? Number(helper.hourly_rate) : null,
      service_radius_miles: helper.service_radius_miles,
      base_postcode: helper.base_postcode,
      years_experience: helper.years_experience,
      instant_book_enabled: !!helper.instant_book_enabled,
      available: !!helper.available,
      verification_status: helper.verification_status,
      id_verified: !!helper.id_verified,
      background_check_passed: !!helper.background_check_passed,
      jobs_completed: Number(helper.jobs_completed ?? 0),
      avg_rating: Number(helper.avg_rating ?? 0),
      rating_count: Number(helper.rating_count ?? 0),
      response_time_minutes: helper.response_time_minutes,
      acceptance_rate: helper.acceptance_rate != null ? Number(helper.acceptance_rate) : null,
      completion_rate: helper.completion_rate != null ? Number(helper.completion_rate) : null,
      repeat_booking_rate: helper.repeat_booking_rate != null ? Number(helper.repeat_booking_rate) : null,
      featured: !!helper.featured,
      trust_score: Number((helper as any).trust_score ?? 0),
      trust_score_updated_at: ((helper as any).trust_score_updated_at as string | null) ?? null,
      created_at: helper.created_at,
      skills: ((skillsRaw ?? []) as any[]).map((s) => s.categories).filter(Boolean),
      badges: ((badgesRaw ?? []) as any[]).map((b) => b.badges).filter(Boolean),
      reviews,
      rating_breakdown: breakdown,
    };
  });
